import React, { useEffect, useState } from "react";
import { questions } from "../data/questions";
import { calculateScore } from "../utils/scoring";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { resultsText } from "../data/resultsText";
import "../styles/assessment.css";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}


function Assessment({ language }: { language: "en" | "vn" }) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const isComplete = step >= questions.length;
  const current = questions[step];
  const assetBase = `/assets/Q${step + 1}`;
  const [videoError, setVideoError] = useState(false);
  const [openModal, setOpenModal] = useState<string | null>(null);

useEffect(() => {
  if (step === 0 && typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "started_assessment", {
      language: language,
    });
  }
}, [step]);

 useEffect(() => {
  setShowOverlay(false);
  setVideoError(false); // reset fallback for next question
  const timer = setTimeout(() => setShowOverlay(true), 1000);
  return () => clearTimeout(timer);
}, [step]);


  const handleSelect = (choice: string) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "answered_question", {
      question_number: step + 1,
      language: language,
    });
  }

  setAnswers({ ...answers, [current.id]: choice });
  setStep(step + 1);
};


if (isComplete) {
  const result = calculateScore(answers);

  if (!result || !result.level || !resultsText?.[language]?.[result.level]) {
    console.error("❌ Missing result or resultsText mapping", { result, language, resultsText });
    return (
      <div style={{ padding: "2rem", color: "black" }}>
        <h1>Error loading results</h1>
        <p>Something went wrong mapping your result to the text block.</p>
      </div>
    );
  }

  const levels = ["junior", "medior", "senior"] as const;
  
   return (
  <>
    <div className="sponsor-bar">
      <span>Made possible by:</span>
      <img src="/assets/NNT.jpg" alt="Logo No-Nonsense Training" style={{ height: "90px" }} />
      <img src="/assets/BWA.png" alt="Logo Betterworks Asia" />
    </div>

    <div className="results-container">
      <div className="results-content">
        <h1>{resultsText[language][result.level].label}</h1>
        <p className="score">Average Score: {result.avg.toFixed(2)}</p>
        <p className="pitch">{resultsText[language][result.level].pitch}</p>

        <div className="training-box">
  <h2>{resultsText[language][result.level].training.title}</h2>
  <p className="training-date">
    {resultsText[language][result.level].training.date}
  </p>
  <p>{resultsText[language][result.level].training.description}</p>
  <p>
    <strong>Price: {resultsText[language][result.level].training.price}</strong>
  </p>
</div>


        <hr style={{ margin: "2rem 0" }} />
        <h2>Compare all levels</h2>
        <div className="comparison-box">
          {levels
            .filter((key) => key !== result.level)
            .map((key) => (
              <div key={key} className="comparison-card">
                <h3>{resultsText[language][key].training.title}</h3>
                <p>{resultsText[language][key].training.teaser}</p>
                <button onClick={() => setOpenModal(key)} className="see-more-button">
                  See more
                </button>
              </div>
          ))}
        </div>

        {openModal && (
  <div className="modal-backdrop" onClick={() => setOpenModal(null)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <button className="modal-close" onClick={() => setOpenModal(null)}>×</button>
      <h2>{resultsText[language][openModal as "junior" | "medior" | "senior"].training.title}</h2>
      <p className="training-date">
        {resultsText[language][openModal as "junior" | "medior" | "senior"].training.date}
      </p>
      <p>{resultsText[language][openModal as "junior" | "medior" | "senior"].training.description}</p>
      <p>
        <strong>Price: {resultsText[language][openModal as "junior" | "medior" | "senior"].training.price}</strong>
      </p>
    </div>
  </div>
)}


        <div style={{ marginTop: "2rem" }}>
          <a
  href="https://docs.google.com/forms/d/e/1FAIpQLSeBcKeD2Ez9CCmDHjmVfhcJ4SHLOx922O3yOS30v_yX7Jdi2g/viewform?usp=sharing&ouid=106198561454088672150"
  target="_blank"
  rel="noopener noreferrer"
  className="cta-button"
>
  Click here to register and receive the invoice!
</a>
        </div>

        <div className="email-box" style={{ marginTop: "3rem", textAlign: "center" }}>
          <label>Interested in facilitation training?</label>
          <input
            type="email"
            placeholder="Enter your email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            onClick={async () => {
              if (!emailSaved && email.trim()) {
                try {
                  console.log("📬 Submitting email:", email, result.level, result.avg);
                  await addDoc(collection(db, "responses"), {
                    email,
                    score: result.avg,
                    level: result.level,
                    language,
                    submittedAt: serverTimestamp(),
                  });
                  setEmailSaved(true);
                } catch (err) {
                  console.error("❌ Error saving email:", err);
                }
              }
            }}
            disabled={emailSaved || !email.trim()}
          >
            {emailSaved ? "Thanks! We’ll keep you posted." : "Keep me informed!"}
          </button>
        </div>
      </div>
    </div>
  </>
);

}

 return (
  <>
    <div className="progress-bar-wrapper">
      <div
        className="progress-bar-fill"
        style={{ width: `${(step / questions.length) * 100}%` }}
      />
    </div>

    <div className="assessment-container">
      <div className="assessment-wrapper">
       <img
  src={`${assetBase}.png`}
  alt={`Visual for question ${step + 1}`}
  className="assessment-image"
/>
        {showOverlay && (
          <div className="overlay slide-up">
            <h2 className="text-xl font-bold mb-4">
              {current.text[language]}
            </h2>
            <div className="w-full max-w-md space-y-2">
              {Object.entries(current.options).map(([key, value]) => (
                <button
                  key={key}
                  className="answer-button"
                  onClick={() => handleSelect(key)}
                >
                  <strong>{key}</strong>: {value[language]}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </>
);

}

export default Assessment;
